import { useState } from 'react';
import { Button, Group, NumberInput, Stack, Text, Textarea } from '@mantine/core';
import { modals } from '@mantine/modals';

import type {
  TH2HPayment,
  TH2HSettlementSource,
} from '../store/types';

type RejectFormProps = {
  paymentId: number;
  fixedReason: string | null;
  onReject: (paymentId: number, reason: string) => void;
};

const H2HPaymentRejectForm = ({ paymentId, fixedReason, onReject }: RejectFormProps) => {
  const [reason, setReason] = useState(fixedReason ?? '');

  return (
    <Stack>
      <Textarea
        label="Причина отклонения"
        value={reason}
        onChange={event => setReason(event.currentTarget.value)}
        minRows={3}
        readOnly={fixedReason !== null}
        required
      />
      {fixedReason !== null && (
        <Text size="sm" c="dimmed">
          Решение уже зафиксировано. Повторная попытка отправит тот же callback без изменения причины.
        </Text>
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={() => modals.closeAll()}>Отмена</Button>
        <Button
          color="red"
          disabled={!reason.trim()}
          onClick={() => {
            onReject(paymentId, reason.trim());
            modals.closeAll();
          }}
        >
          Отклонить
        </Button>
      </Group>
    </Stack>
  );
};

type Props = {
  payment: TH2HPayment;
  isLoading?: boolean;
  onCredit: (paymentId: number, paidAmount: string) => void;
  onFinalize: (paymentId: number, settlementSource: TH2HSettlementSource) => void;
  onReject: (paymentId: number, reason: string) => void;
  onResolveProviderConflict: (paymentId: number) => void;
  onReviewDiscrepancy: (paymentId: number) => void;
  onHideFromQueue: (paymentId: number) => void;
  showHideFromQueue: boolean;
};

export const H2HPaymentActions = ({
  payment,
  isLoading,
  onCredit,
  onFinalize,
  onReject,
  onResolveProviderConflict,
  onReviewDiscrepancy,
  onHideFromQueue,
  showHideFromQueue,
}: Props) => {
  const {
    paymentId,
    workflowStatus: status,
    baseAmount: amount,
    baseCurrency: currency,
    providerAmount,
    providerCurrency,
    clientCreditedAt,
    clientCreditedAmount,
    merchantCallbackIntentStatus,
    merchantCallbackIntentPaidAmount,
    merchantCallbackIntentReason,
    merchantCallbackStatus,
    availableActions,
  } = payment;
  const isLegacySettledCallbackRetry = status === 'completed'
    && clientCreditedAt === null
    && merchantCallbackStatus !== 'success'
    && merchantCallbackIntentStatus === 'approved'
    && merchantCallbackIntentPaidAmount !== null;
  const canCredit = availableActions.includes('credit_client');
  const canReject = availableActions.includes('reject');
  const canFinalize = availableActions.includes('finalize');
  const canResolveProviderConflict = availableActions.includes('resolve_provider_conflict');
  const canReviewDiscrepancy = availableActions.includes('review_discrepancy');
  const canHideFromQueue = showHideFromQueue && availableActions.includes('hide_from_queue');
  const creditedAmount = clientCreditedAmount ?? amount;
  const creditAmount = merchantCallbackIntentPaidAmount ?? amount;

  const openCreditConfirmation = () => modals.openConfirmModal({
    title: isLegacySettledCallbackRetry
      ? 'Повторить зачисление клиенту'
      : 'Зачислить клиенту',
    centered: true,
    children: (
      <Text>
        {isLegacySettledCallbackRetry
          ? `Магазин и партнёр уже получили начисление в старом процессе. Будет повторно отправлен только APPROVED callback на ${creditAmount} ${currency}; балансы повторно не изменятся.`
          : `Клиент получит ${creditAmount} ${currency}. Магазин и партнёр пока не получат начисление — оно произойдёт только после AvePay COMPLETED и выбора валюты расчёта.`}
      </Text>
    ),
    labels: {
      confirm: isLegacySettledCallbackRetry
        ? 'Повторить зачисление'
        : 'Зачислить клиенту',
      cancel: 'Отмена',
    },
    confirmProps: { color: 'green' },
    onConfirm: () => onCredit(paymentId, creditAmount),
  });

  const openFinalizeConfirmation = (
    settlementAmount: string,
    settlementCurrency: string,
    settlementSource: TH2HSettlementSource,
  ) => {
    const useProviderAmount = settlementSource === 'provider_callback';

    modals.openConfirmModal({
      title: `Зачислить магазину в ${settlementCurrency}`,
      centered: true,
      children: (
        <Stack gap="sm">
          <NumberInput
            label={`Сумма расчёта (${settlementCurrency})`}
            value={settlementAmount}
            thousandSeparator=" "
            readOnly
          />
          <Text size="sm">
            {useProviderAmount
              ? providerCurrency === currency
                ? `Магазин и партнёр получат ${settlementAmount} ${settlementCurrency} — фактическую сумму callback AvePay.`
                : `Магазин и партнёр получат начисление в ${settlementCurrency} из фактической суммы callback AvePay. В ${currency} магазин ничего не получит.`
              : `Магазин и партнёр получат начисление в ${currency} из фактически зачисленной клиенту суммы. Сумма callback AvePay в ${providerCurrency ?? 'валюте провайдера'} не будет начислена магазину.`}
          </Text>
          <Text size="sm" fw={500}>Клиент уже получил {creditedAmount} {currency}.</Text>
        </Stack>
      ),
      labels: {
        confirm: useProviderAmount && providerCurrency === currency
          ? 'Зачислить по callback AvePay'
          : `Зачислить в ${settlementCurrency}`,
        cancel: 'Отмена',
      },
      confirmProps: { color: useProviderAmount ? 'green' : 'blue' },
      onConfirm: () => onFinalize(paymentId, settlementSource),
    });
  };

  const openRejectModal = () => modals.open({
    title: 'Отклонить H2H-платёж',
    centered: true,
    children: (
      <H2HPaymentRejectForm
        paymentId={paymentId}
        fixedReason={merchantCallbackIntentReason}
        onReject={onReject}
      />
    ),
  });

  const openResolveProviderConflictConfirmation = () => modals.openConfirmModal({
    title: 'Подтвердить первый COMPLETED',
    centered: true,
    children: (
      <Text size="sm">
        AvePay прислал противоречащие друг другу callback. Будет принят первый проверенный COMPLETED; действие сохранится в аудите.
      </Text>
    ),
    labels: { confirm: 'Подтвердить', cancel: 'Отмена' },
    confirmProps: { color: 'orange' },
    onConfirm: () => onResolveProviderConflict(paymentId),
  });

  const openReviewDiscrepancyConfirmation = () => modals.openConfirmModal({
    title: 'Отметить расхождение проверенным?',
    centered: true,
    children: (
      <Text size="sm">
        Платёж не будет удалён. Суммы, статусы зачисления и балансы не изменятся. Заявка останется во вкладке «Всё».
      </Text>
    ),
    labels: { confirm: 'Проверено', cancel: 'Отмена' },
    confirmProps: { color: 'gray' },
    onConfirm: () => onReviewDiscrepancy(paymentId),
  });

  const openHideFromQueueConfirmation = () => modals.openConfirmModal({
    title: 'Убрать заявку из рабочих разделов?',
    centered: true,
    children: (
      <Text size="sm">
        Заявка не будет удалена: она останется доступной во вкладке «Всё», но исчезнет из «Требует действия», «Ожидает коллбэк» и «Расхождение».
      </Text>
    ),
    labels: { confirm: 'Убрать из разделов', cancel: 'Отмена' },
    confirmProps: { color: 'gray' },
    onConfirm: () => onHideFromQueue(paymentId),
  });

  if (
    !canCredit
    && !canReject
    && !canFinalize
    && !canResolveProviderConflict
    && !canReviewDiscrepancy
    && !canHideFromQueue
  ) return null;

  return (
    <Group gap="xs" justify="flex-end">
      {canCredit && (
        <Button
          size="xs"
          color="green"
          loading={isLoading}
          onClick={openCreditConfirmation}
        >
          {isLegacySettledCallbackRetry
            ? 'Повторить зачисление клиенту'
            : 'Зачислить клиенту'}
        </Button>
      )}
      {canFinalize && (
        <>
          <Button
            size="xs"
            loading={isLoading}
            onClick={() => openFinalizeConfirmation(
              creditedAmount,
              currency,
              'client_credited',
            )}
          >
            Зачислить в {currency}
          </Button>
          {providerAmount !== null
            && providerCurrency !== null
            && (providerAmount !== creditedAmount || providerCurrency !== currency) && (
            <Button
              size="xs"
              color="green"
              loading={isLoading}
              onClick={() => openFinalizeConfirmation(
                providerAmount,
                providerCurrency,
                'provider_callback',
              )}
            >
              {providerCurrency === currency
                ? 'Зачислить по callback AvePay'
                : `Зачислить в ${providerCurrency}`}
            </Button>
          )}
        </>
      )}
      {canResolveProviderConflict && (
        <Button
          size="xs"
          color="orange"
          variant="outline"
          loading={isLoading}
          onClick={openResolveProviderConflictConfirmation}
        >
          Подтвердить первый COMPLETED
        </Button>
      )}
      {canReviewDiscrepancy && (
        <Button
          size="xs"
          color="gray"
          variant="outline"
          loading={isLoading}
          onClick={openReviewDiscrepancyConfirmation}
        >
          Проверено
        </Button>
      )}
      {canReject && (
        <Button
          size="xs"
          color="red"
          variant="outline"
          loading={isLoading}
          onClick={openRejectModal}
        >
          Отклонить
        </Button>
      )}
      {canHideFromQueue && (
        <Button
          size="xs"
          color="gray"
          variant="subtle"
          loading={isLoading}
          onClick={openHideFromQueueConfirmation}
        >
          Убрать из раздела
        </Button>
      )}
    </Group>
  );
};
